import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react';
import { appConfig } from './appConfig';

/**
 * Central registry of the Unleash flags this portal reads.
 *
 * Mirrors gigmosaic-provider-dashboard/src/config/featureFlags.ts so both
 * frontends declare their flags in one place. Names are kebab-case and must
 * match Unleash exactly (project `default`, environment `production`) — the
 * frontend token decides which project/environment this app is served.
 *
 * A name that does not exist in Unleash — or one that has been archived —
 * evaluates to `false`. That is why every read goes through `useFeatureFlag`
 * below rather than calling `useFlag` directly.
 */
export const FEATURE_FLAGS = {
  /** Gates the cash-on-hand package checkout (PR #1481). */
  cashOnHandCheckout: 'demo-cash-on-hand-checkout',
  /** Gates the redesigned quotation flow (PR #1482). */
  quotationFlowV2: 'demo-quotation-flow-v2',
  /** Hides card payment on the slot booking flow. ON = hidden. */
  hideSlotBookingCardPayment: 'hp-rel-hide-slot-booking-card-payment',
  /** Gates the multi-provider broadcast quote request feature. */
  quoteBroadcast: 'quote-broadcast',
  /**
   * Gates the Prowl v2 rewire (single SSE agentic stream, real tool-calling)
   * for the customer portal. Default OFF — with it off, ProwlChatWidget and
   * the homepage concierge panel must behave byte-for-byte as before this
   * flag existed. Name matches hp-ai-service's `isEnabled('prowl.v2.customer')`
   * verbatim (design-spec §13) — flag names must match Unleash exactly.
   */
  prowlV2Customer: 'prowl.v2.customer',
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

/**
 * Reads a flag from Unleash, fail-open.
 *
 *   fetched, flag enabled   -> true
 *   fetched, flag disabled  -> false   (the kill switch)
 *   never fetched yet       -> true    (cold start / Unleash unreachable)
 *   outage AFTER a fetch    -> last known value
 *
 * `flagsReady` only flips true after a *successful* fetch, so "not ready"
 * covers a cold start with no network as well as a slow first load. In both
 * cases the gated feature stays usable.
 *
 * Once a fetch has succeeded we trust the fetched value: the SDK persists
 * toggles to localStorage and keeps polling every 30s, recovering on its own.
 * A deliberate kill therefore stays in force through a transient blip instead
 * of silently switching the feature back on.
 */
const useUnleashFlag = (flagName: string): boolean => {
  const { flagsReady } = useFlagsStatus();
  const enabled = useFlag(flagName);
  return flagsReady ? enabled : true;
};

/** Used when Unleash is not configured — every flag reads as ON. */
const useAlwaysOn = (_flagName: string): boolean => true;

/**
 * Read a feature flag.
 *
 * The implementation is chosen once at module load, never per render, so the
 * hook call order stays stable for the lifetime of the app (swapping it per
 * render would violate the rules of hooks).
 *
 * When `VITE_UNLEASH_FRONTEND_URL` / `VITE_UNLEASH_CLIENT_KEY` are absent,
 * main.tsx does not mount `FlagProvider` — calling `useFlag` outside that
 * provider would throw — so this resolves to `useAlwaysOn` instead and every
 * gated feature stays on.
 *
 * NOTE: this deliberately diverges from the provider dashboard's
 * `RequireFeature`, which fails *closed* (it renders a spinner until
 * `flagsReady`, so a sustained Unleash outage leaves gated routes spinning
 * forever). That behaviour is not acceptable on the customer checkout path.
 */
export const useFeatureFlag: (flagName: string) => boolean = appConfig.unleash
  .isConfigured
  ? useUnleashFlag
  : useAlwaysOn;
